import { theme, ThemeConfig } from "antd";
import { ThemeMode } from "./lib/ThemeContext";

// Light theme configuration
const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  // token: {
  //   colorPrimary: "#33312F",
  //   colorPrimaryHover: "#4A4846",
  //   colorPrimaryActive: "#33312F",
  //   colorPrimaryBorder: "#33312F",

  //   // Background colors
  //   colorBgBase: "#FFFFFF",
  //   colorBgContainer: "#FFFFFF",
  //   colorBgElevated: "#FFFFFF",
  //   colorBgLayout: "#F5F5F5",

  //   // Text colors
  //   colorText: "#333333",
  //   colorTextSecondary: "#666666",
  //   colorTextTertiary: "#999999",
  //   colorTextQuaternary: "#CCCCCC",

  //   // Border colors
  //   colorBorder: "#D9D9D9",
  //   colorBorderSecondary: "#F0F0F0",
  // },
  // components: {
  //   Menu: {
  //     itemBg: "#FFFFFF",
  //     itemSelectedBg: "#33312F",
  //     itemHoverBg: "#F5F5F5",
  //     itemColor: "#666666",
  //     itemSelectedColor: "#FFFFFF",
  //     itemHoverColor: "#333333",
  //     groupTitleColor: "#666666",
  //   },
  //   Layout: {
  //     headerBg: "#FFFFFF",
  //     siderBg: "#FFFFFF",
  //     bodyBg: "#F5F5F5",
  //   },
  //   Card: {
  //     colorBgContainer: "#FFFFFF",
  //   },
  //   Table: {
  //     colorBgContainer: "#FFFFFF",
  //     headerBg: "#FAFAFA",
  //   },
  // },
  token: {
    // ------------------------------------
    // Global Color Tokens
    // ------------------------------------
    colorPrimary: "#635bff",
    colorSuccess: "#00ca72",
    colorWarning: "#f59e0b",
    colorError: "#e11d48",
    colorInfo: "#0ea5e9",

    colorTextBase: "#4f566b",
    colorTextHeading: "#1a1f36",
    colorTextSecondary: "#697386",
    colorTextQuaternary: "#a3acb9", // Used for placeholders/icons

    colorBgLayout: "#f6f9fc",
    colorBgContainer: "#ffffff",
    colorBgElevated: "#ffffff",

    colorBorder: "#e3e8ee",
    colorBorderSecondary: "#f1f5f9",

    // ------------------------------------
    // Typography
    // ------------------------------------
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
    fontSizeHeading1: 28,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,

    // ------------------------------------
    // Shape & Size
    // ------------------------------------
    borderRadius: 6, // Matches 'rounded-md'
    borderRadiusLG: 12, // Matches 'rounded-xl'
    borderRadiusSM: 4,

    controlHeight: 40, // Base height for inputs/buttons (approx py-2.5)
    controlHeightLG: 48,
    controlHeightSM: 32,

    // ------------------------------------
    // Effect
    // ------------------------------------
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)", // Matches 'shadow-sm'
    boxShadowSecondary: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    lineWidth: 1,
  },
  components: {
    // ------------------------------------
    // Component Overrides
    // ------------------------------------
    Button: {
      colorPrimary: "#635bff",
      algorithm: true, // Generate hover/active states automatically
      fontWeight: 600,
      defaultShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      primaryShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      contentFontSize: 14,
      borderRadius: 6,
    },
    Input: {
      colorBgContainer: "#ffffff",
      colorBorder: "#e3e8ee",
      activeBorderColor: "#635bff",
      hoverBorderColor: "#cfd7df", // Inferred slightly darker border on hover
      activeShadow: "0 0 0 2px rgba(99, 91, 255, 0.2)", // Matches 'ring-2 ring-[#635bff]/20'
      borderRadius: 8, // Inputs often use 'rounded-lg' in this codebase
      controlHeight: 42,
      paddingInline: 12,
    },
    Select: {
      colorBorder: "#e3e8ee",
      colorPrimary: "#635bff",
      controlHeight: 42,
      borderRadius: 8,
    },
    Card: {
      colorBgContainer: "#ffffff",
      headerBg: "#ffffff",
      colorBorderSecondary: "#e3e8ee",
      borderRadiusLG: 12, // 'rounded-xl'
      boxShadowTertiary: "0 1px 3px rgba(0,0,0,0.1)", // Custom card shadow
      paddingLG: 24, // 'p-6'
    },
    Modal: {
      borderRadiusLG: 12,
      headerBg: "#ffffff",
      contentBg: "#ffffff",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", // Deep shadow from CSS
    },
    Table: {
      headerBg: "#fcfdfe",
      headerColor: "#9ba3b2", // Muted uppercase headers
      headerSplitColor: "#e3e8ee",
      borderColor: "#e3e8ee",
      rowHoverBg: "#f6f9fc",
      headerBorderRadius: 0,
    },
    Menu: {
      itemBg: "#ffffff",
      itemColor: "#4f566b",
      itemSelectedColor: "#635bff", // Text becomes primary
      itemSelectedBg: "#f6f9fc", // Background becomes subtle
      itemHoverBg: "#f6f9fc",
      itemBorderRadius: 6,
      itemMarginInline: 8,
    },
    Tabs: {
      itemColor: "#697386",
      itemSelectedColor: "#635bff",
      itemHoverColor: "#1a1f36",
      inkBarColor: "#635bff",
      titleFontSize: 13,
    },
    Tag: {
      borderRadius: 4,
      defaultBg: "#f6f9fc",
      defaultColor: "#4f566b",
      colorBorder: "#e3e8ee",
    },
    Badge: {
      colorError: "#e11d48",
      colorSuccess: "#00ca72",
      colorWarning: "#f59e0b",
      colorInfo: "#0ea5e9",
      textFontSize: 10,
    },
    Alert: {
      colorInfoBg: "#f0f9ff",
      colorInfoBorder: "#bae6fd",
      colorSuccessBg: "#e3fcf0",
      colorSuccessBorder: "#bbf7d0",
      colorWarningBg: "#fff7ed",
      colorWarningBorder: "#fed7aa",
      colorErrorBg: "#fff1f2",
      colorErrorBorder: "#fecdd3",
      borderRadiusLG: 8,
    },
    Divider: {
      colorSplit: "#e3e8ee",
    },
    Typography: {
      colorTextHeading: "#1a1f36",
      colorText: "#4f566b",
      colorTextDescription: "#697386",
      fontWeightStrong: 700,
    },
  },
};

// Dark theme configuration
const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: "#1890FF",
    colorPrimaryHover: "#40A9FF",
    colorPrimaryActive: "#096DD9",
    colorPrimaryBorder: "#1890FF",

    // Background colors
    colorBgBase: "#141414",
    colorBgContainer: "#1F1F1F",
    colorBgElevated: "#262626",
    colorBgLayout: "#000000",

    // Text colors
    colorText: "#FFFFFF",
    colorTextSecondary: "#B3B3B3",
    colorTextTertiary: "#8C8C8C",
    colorTextQuaternary: "#595959",

    // Border colors
    colorBorder: "#434343",
    colorBorderSecondary: "#303030",
  },
  components: {
    Menu: {
      itemBg: "#1F1F1F",
      itemSelectedBg: "#1890FF",
      itemHoverBg: "#262626",
      itemColor: "#B3B3B3",
      itemSelectedColor: "#FFFFFF",
      itemHoverColor: "#FFFFFF",
      groupTitleColor: "#8C8C8C",
    },
    Layout: {
      headerBg: "#1F1F1F",
      siderBg: "#1F1F1F",
      bodyBg: "#141414",
    },
    Card: {
      colorBgContainer: "#1F1F1F",
    },
    Table: {
      colorBgContainer: "#1F1F1F",
      headerBg: "#262626",
    },
    Button: {
      colorBgContainer: "#1F1F1F",
      colorBorder: "#434343",
    },
    Input: {
      colorBgContainer: "#1F1F1F",
      colorBorder: "#434343",
    },
    Select: {
      colorBgContainer: "#1F1F1F",
      colorBorder: "#434343",
    },
    DatePicker: {
      colorBgContainer: "#1F1F1F",
      colorBorder: "#434343",
    },
    Modal: {
      contentBg: "#1F1F1F",
      headerBg: "#1F1F1F",
    },
    Drawer: {
      colorBgElevated: "#1F1F1F",
    },
    Tooltip: {
      colorBgSpotlight: "#262626",
    },
    Popover: {
      colorBgElevated: "#1F1F1F",
    },
    Dropdown: {
      colorBgElevated: "#1F1F1F",
    },
  },
};

export const getTheme = (mode: ThemeMode): ThemeConfig => {
  return mode === "dark" ? darkTheme : lightTheme;
};

// Export default theme for backward compatibility
export default lightTheme;
